import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
  type ErrorRouteComponent,
} from '@tanstack/react-router';
import {
  QueryClient,
  QueryClientProvider,
  MutationCache,
} from '@tanstack/react-query';
import {toast} from 'sonner';
import {
  isFlowNotReadyError,
  FLOW_NOT_READY_MESSAGE,
} from '@features/flow/utils/flowNotReady';
import {NuqsAdapter} from 'nuqs/adapters/tanstack-router';
import {AnimatePresence} from 'framer-motion';
import {Suspense, useEffect} from 'react';
import ReactGA from 'react-ga4';
import {ReactFlowProvider} from '@xyflow/react';
import {captureException} from '@sentry/react';
import {NextUIProvider} from '@nextui-org/react';
import {TooltipProvider} from '@features/ui/components/tooltip';
import {Toaster} from '@features/ui/components/sonner';
import {TRPCProvider} from '@features/providers/TRPCProvider';
import {ActiveScreenIdProvider} from '@features/activeScreenIdContext/ActiveScreenIdProvider';
import amplitudeService from '@features/analytics/amplitudeService';
import {cn} from '@features/ui/utils';
import appCss from '@features/globals.css?url';
import '@xyflow/react/dist/style.css';
import {FlowNotFoundScreen} from '@features/ui/components/FlowNotFoundScreen';
import {EmptyState} from '@features/chat/components/EmptyState';
const DubAnalytics = (await import('@dub/analytics/react')).Analytics;

const NO_RETRY_CODES = new Set(['NOT_FOUND', 'FORBIDDEN', 'UNAUTHORIZED']);

const isExpectedTrpcFailure = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'data' in error &&
  typeof error.data === 'object' &&
  error.data !== null &&
  'code' in error.data &&
  typeof error.data.code === 'string' &&
  NO_RETRY_CODES.has(error.data.code);

const queryClient = new QueryClient({
  // Surface the "flow still being set up" state (screen mutations on a
  // components flow with no committed version yet) as a friendly toast for
  // every mutation, instead of a silent dead-end / unhandled rejection.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (isFlowNotReadyError(error)) {
        toast.warning(FLOW_NOT_READY_MESSAGE);
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isExpectedTrpcFailure(error)) return false;
        return failureCount < 3;
      },
    },
  },
});

const Error: ErrorRouteComponent = ({error, reset, info}) => {
  useEffect(() => {
    captureException(error, {
      extra: {stack: info?.componentStack ?? 'no stack'},
    });
  }, [error, info]);

  return (
    <html>
      <body>
        <EmptyState
          title='We encountered an error'
          subtitle='Something went wrong. We recorded this error. You can try again - and if this error persists, please reach out to support!'
          action={{
            href: '#',
            label: 'Reset Page',
            onClick: reset,
          }}
        />
      </body>
    </html>
  );
};

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {charSet: 'utf-8'},
      {name: 'viewport', content: 'width=device-width, initial-scale=1'},
      {title: 'Banani | Home'},
    ],
    links: [
      {rel: 'stylesheet', href: appCss},
      {rel: 'preconnect', href: 'https://rsms.me/', crossOrigin: 'anonymous'},
      {
        rel: 'stylesheet',
        href: 'https://rsms.me/inter/inter.css',
        crossOrigin: 'anonymous',
      },
      {rel: 'preconnect', href: 'https://fonts.googleapis.com'},
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@100..900&family=Geist:wght@100..900&family=IBM+Plex+Mono:wght@100..700&family=IBM+Plex+Sans:wght@100..700&family=Nunito:wght@200..900&family=PT+Serif:wght@400;700&family=Roboto+Slab:wght@100..900&family=Shantell+Sans:wght@300..800&family=Space+Grotesk:wght@300..700&display=swap',
      },
    ],
  }),
  errorComponent: Error,
  component: RootLayout,
  notFoundComponent: () => {
    return <FlowNotFoundScreen variant='generic' />;
  },
});

function RootLayout() {
  useEffect(() => {
    ReactGA.initialize('G-8J53SBTG3Y');
    void amplitudeService.init();
  }, []);

  return (
    <html lang='en' className='h-full'>
      <head>
        <HeadContent />
        <link rel='icon' href='/icon.png' sizes='384x384' type='image/png' />
        <link
          rel='icon'
          href='/favicon.ico'
          sizes='64x64'
          type='image/x-icon'
        />
        <link
          rel='apple-touch-icon'
          href='/apple-icon.png'
          sizes='384x384'
          type='image/png'
        />
      </head>
      <body
        className={cn('h-full')}
        suppressHydrationWarning={true}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'none';
        }}
        onDrop={(e) => {
          e.preventDefault();
        }}
      >
        <AnimatePresence>
          <QueryClientProvider client={queryClient}>
            <TRPCProvider>
              <NextUIProvider className='h-full'>
                <Suspense>
                  <NuqsAdapter>
                    <ActiveScreenIdProvider>
                      <TooltipProvider delayDuration={0}>
                        <ReactFlowProvider>
                          <Outlet />
                          <Toaster />
                        </ReactFlowProvider>
                      </TooltipProvider>
                    </ActiveScreenIdProvider>
                  </NuqsAdapter>
                </Suspense>
              </NextUIProvider>
            </TRPCProvider>
            {/* <ReactQueryDevtools initialIsOpen={false} /> */}
          </QueryClientProvider>
        </AnimatePresence>
        <DubAnalytics domainsConfig={{refer: 'getbanani.link'}} />
        <Scripts />
      </body>
    </html>
  );
}
